// ====================================================
// VidyaTrack – Authentication Context
// Uses secondaryAuth for account creation so the
// Admin session is NEVER interrupted.
// ====================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { auth, secondaryAuth, db } from "../firebase/config";
import { sanitiseDoc } from "../utils/helpers";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// ── Friendly error messages ──────────────────────────
const ERROR_MAP = {
  "auth/invalid-credential"    : "Invalid email or password. Please try again.",
  "auth/wrong-password"        : "Incorrect password. Please try again.",
  "auth/user-not-found"        : "No account found with this email address.",
  "auth/invalid-email"         : "Please enter a valid email address.",
  "auth/email-already-in-use"  : "This email address is already registered.",
  "auth/weak-password"         : "Password must be at least 6 characters.",
  "auth/too-many-requests"     : "Too many failed attempts. Please wait a few minutes.",
  "auth/network-request-failed": "Network error. Please check your internet connection.",
  "auth/user-disabled"         : "This account has been suspended. Contact your administrator.",
  "auth/operation-not-allowed" : "Email/password login is not enabled. Contact your administrator.",
};
const friendly = (code) => ERROR_MAP[code] ?? "Authentication failed. Please try again.";

// ============================================================
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData,    setUserData]    = useState(null);
  const [loading,     setLoading]     = useState(true);

  // ── LOGIN ────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password
    ).catch(err => { throw new Error(friendly(err.code)); });

    const snap = await getDoc(doc(db, "users", cred.user.uid)).catch(() => null);
    if (!snap || !snap.exists()) {
      await signOut(auth);
      throw new Error("Your account profile was not found. Please contact your administrator.");
    }

    const data = sanitiseDoc(snap.data());
    setCurrentUser(cred.user);
    setUserData(data);
    return { success: true, role: data.role };
  }, []);

  // ── LOGOUT ───────────────────────────────────────────────
  const logout = useCallback(async () => {
    await signOut(auth).catch(() => {});
    setCurrentUser(null);
    setUserData(null);
  }, []);

  // ── REGISTER ─────────────────────────────────────────────
  // Uses secondaryAuth so the Admin is NOT logged out.
  // After the new account is created, we immediately sign
  // the secondary session out.
  const register = useCallback(async (formData) => {
    // 1. Create the Firebase Auth user on the SECONDARY instance
    const cred = await createUserWithEmailAndPassword(
      secondaryAuth,
      formData.email.trim().toLowerCase(),
      formData.password
    ).catch(err => { throw new Error(friendly(err.code)); });

    // 2. Optionally set the display name on the secondary user
    if (formData.name) {
      await updateProfile(cred.user, { displayName: formData.name }).catch(() => {});
    }

    // 3. Write the Firestore user profile (uses primary db — no auth needed)
    const profile = {
      uid      : cred.user.uid,
      email    : formData.email.trim().toLowerCase(),
      name     : formData.name || "",
      role     : formData.role || "student",
      phone    : formData.phone || "",
      school   : formData.school || "",
      isActive : true,
      createdAt: serverTimestamp(),
      // Role-specific extra fields
      ...(formData.role === "teacher" && {
        subject : formData.subject || "",
        classes : formData.classes || [],
      }),
      ...(formData.role === "student" && {
        rollNumber : formData.rollNumber || "",
        classId    : formData.classId    || "",
        className  : formData.className  || "",
        section    : formData.section    || "",
        parentId   : formData.parentId   || "",
      }),
      ...(formData.role === "parent" && {
        children  : formData.children  || [],
        childName : formData.childName || "",
        childClass: formData.childClass|| "",
      }),
    };

    await setDoc(doc(db, "users", cred.user.uid), profile)
      .catch(err => { throw new Error("Profile creation failed: " + err.message); });

    // 4. Sign out of the SECONDARY instance immediately
    //    The primary (Admin) session is completely unaffected.
    await signOut(secondaryAuth).catch(() => {});

    return { success: true, uid: cred.user.uid };
  }, []);

  // ── PASSWORD RESET ───────────────────────────────────────
  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email.trim().toLowerCase())
      .catch(err => { throw new Error(friendly(err.code)); });
    return { success: true };
  }, []);

  // ── UPDATE PROFILE ────────────────────────────────────────
  const updateUserData = useCallback(async (patch) => {
    if (!currentUser) return;
    setUserData(prev => ({ ...prev, ...patch }));
    await updateDoc(doc(db, "users", currentUser.uid), {
      ...patch,
      updatedAt: serverTimestamp(),
    }).catch(() => {});
  }, [currentUser]);

  // ── AUTH STATE LISTENER ───────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            setUserData(sanitiseDoc(snap.data()));
          } else {
            // No Firestore profile → sign out gracefully
            await signOut(auth);
            setCurrentUser(null);
            setUserData(null);
          }
        } catch {
          // Firestore offline — keep session alive with minimal data
          setUserData(prev => prev ?? {
            uid  : user.uid,
            email: user.email,
            name : user.displayName || user.email,
            role : "student",
          });
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsub;
  }, []);

  return (
    <AuthContext.Provider value={{
      currentUser, userData, loading,
      login, logout, register, resetPassword, updateUserData,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
